<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { ISbStoryData } from '@storyblok/js';

	interface Props {
		content: ISbStoryData<{
			title: string;
			description: string;
			input_placeholder: string;
			cta_label: string;
		}>['content'];
		active: boolean;
		errorMessage?: string;
		submitting?: boolean;
		onclearerror?: () => void;
	}

	let { content, active = false, errorMessage, onclearerror, submitting = false }: Props = $props();
	let inputField = $state<HTMLInputElement | null>(null);

	$effect(() => {
		if (active) {
			inputField?.focus();
		}
	});
</script>

<section
	class={[
		'h-full w-full bg-white text-center',
		{ hidden: !active, 'max-lg:flex max-lg:flex-col max-lg:justify-between': active }
	]}
	use:editable={content}
>
	<div>
		<h1
			class="text-balance font-rhymes text-[28px]/[120%] tracking-[-.28px] lg:mt-18 lg:text-[32px]/[120%] lg:tracking-[-.32px]"
		>
			{content.title}
		</h1>

		{#if content.description?.length}
			<p class="mt-2 text-balance text-[14px]/[160%] lg:mt-3 lg:text-[16px]/[150%]">
				{content.description}
			</p>
		{/if}

		<div class="relative mt-4 text-[16px]/[140%] tracking-[-.16px] lg:mt-9">
			<input
				bind:this={inputField}
				type="email"
				id="email"
				name="email"
				class={[
					'w-full rounded-[52px] border-0 bg-brown-6 px-7 py-5 outline outline-1 outline-brown-5 placeholder:text-brown-4 focus:outline-brown-1',
					{
						'text-error-feedback-critical caret-error-feedback-critical placeholder:text-error-feedback-critical':
							!!errorMessage
					}
				]}
				placeholder={content.input_placeholder}
				required
				oninput={() => onclearerror?.()}
			/>
			{#if errorMessage}
				<p class="mt-2 text-left text-[12px] text-error-feedback-critical">{errorMessage}</p>
			{/if}
		</div>
	</div>
	<Button
		class="btn-dark-grey btn-large mb-5 mt-12 max-lg:w-full lg:mt-[38px]"
		type="submit"
		disabled={submitting}
	>
		<span class="relative">
			<span class={{ invisible: submitting }}>{content.cta_label}</span>
			{#if submitting}
				<span class="absolute inset-0 flex items-center justify-center">
					<span class="flex gap-1">
						<span class="h-1.5 w-1.5 animate-[bounce_1s_infinite] rounded-full bg-white"></span>
						<span class="h-1.5 w-1.5 animate-[bounce_1s_infinite_.1s] rounded-full bg-white"></span>
						<span class="h-1.5 w-1.5 animate-[bounce_1s_infinite_.2s] rounded-full bg-white"></span>
					</span>
				</span>
			{/if}
		</span>
	</Button>
</section>
